import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { AppService } from '@shared/services/app.service';
import { MainQuery } from '@shared/store/main/main.query';

export const generateCustomIndexFn =
  (basePath: string) => (tabs: TabGroupConfig[], activeRoute: string) => {
    let index = 0;

    tabs.forEach((tab, i) => {
      if (activeRoute.includes(`${basePath}/${tab.route}`)) {
        index = i;
      }
    });

    return index;
  };

export interface TabGroupConfig {
  badge?: { amount$: Observable<number>; tooltip: string };
  flag?: string;
  label: string;
  route: string;
  show?: Observable<boolean>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, AsyncPipe, RouterLink, TooltipDirective],
  selector: 'aux-route-tab-group',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  templateUrl: './route-tab-group.component.html',
})
export class RouteTabGroupComponent {
  appService = inject(AppService);
  mainQuery = inject(MainQuery);

  tabs$ = new BehaviorSubject<TabGroupConfig[]>([]);

  activeIndex$ = combineLatest([this.tabs$, this.appService.activeRoute$]).pipe(
    map(([tabs, activeRoute]) => {
      if (this.indexFn) {
        return this.indexFn(tabs, activeRoute);
      }

      let index = 0;

      tabs.forEach((tab, i) => {
        if (activeRoute.includes(tab.route)) {
          index = i;
        }
      });

      return index;
    })
  );

  @Input() className = '';
  @Input() forceFullLayout = false;
  @Input() ignoreFullLayout = false;
  @Input() indexFn: ((tabs: TabGroupConfig[], activeRoute: string) => number) | null = null;
  isFullPage$ = this.mainQuery
    .select('fullPage')
    .pipe(
      map((isFullPage) =>
        this.ignoreFullLayout ? false : this.forceFullLayout ? true : isFullPage
      )
    );
  @Input() variant: 'button' | 'classic' = 'classic';

  get isButtonVariant(): boolean {
    return this.variant === 'button';
  }

  get isClassicVariant(): boolean {
    return this.variant === 'classic';
  }

  @Input() set tabs(tabs: TabGroupConfig[]) {
    this.tabs$.next([...tabs.map((tab) => ({ ...tab, activated: false }))]);
  }
}
